import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Spinner, ProgressBar, Button } from 'react-bootstrap';
import BrokerApiService from '../../services/brokerApiService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faDollarSign,
  faChartLine,
  faArrowUp,
  faArrowDown,
  faBolt,
  faRocket,
  faGraduationCap,
  faCrown,
  faBuilding,
  faCalendarAlt,
  faArrowTrendUp,
  faHandHoldingDollar,
  faFileContract,
  faFolderOpen
} from '@fortawesome/free-solid-svg-icons';
import ActiveTransactionsWidget from './ActiveTransactionsWidget';
import { calculateMonthlyTotal, getCurrentTier } from '../../constants/brokeragePricing';

interface RecentActivity {
  type: 'upgrade' | 'join' | 'downgrade';
  agent: string;
  plan: string;
  date: string;
}

interface DashboardData {
  totalAgents: number;
  activeAgents: number;
  essentialAgents: number;
  professionalAgents: number;
  dealAIAgents: number;
  agentKairosAgents: number;
  premiumAgents: number; // Legacy support
  monthlyRevenue: number;
  annualRevenue: number;
  upgradeRate: number;
  growthRate: number;
  planUtilization: number;
  recentActivity: RecentActivity[];
  baseFee: number;
  agentFees: number;
  perAgentFee: number;
  tierName: string;
}

interface BrokerDashboardProps {
  brokerageId?: string;
  data?: any;
  loading?: boolean;
}

const BrokerDashboard: React.FC<BrokerDashboardProps> = ({ 
  brokerageId, 
  data, 
  loading = false 
}) => {
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalAgents: 0,
    activeAgents: 0,
    essentialAgents: 0,
    professionalAgents: 0,
    dealAIAgents: 0,
    agentKairosAgents: 0,
    premiumAgents: 0,
    monthlyRevenue: 500, // Base broker fee (stability tier for 0 agents)
    annualRevenue: 6000, // Base broker fee annually (stability tier)
    upgradeRate: 0,
    growthRate: 0,
    planUtilization: 0,
    recentActivity: [],
    baseFee: 500, // Stability tier base fee
    agentFees: 0, // No agents initially
    perAgentFee: 70, // Per agent fee
    tierName: 'Stability Plan' // Default tier name
  });
  const [agents, setAgents] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Temporarily commented out billing API call - not needed for dashboard
        /*
        if (!brokerageId) {
          setError('No brokerage account found. Please complete your brokerage setup.');
          return;
        }
        
        const billingData = await BrokerApiService.getBillingInfo(brokerageId);
        
        if (billingData.success) {
          const billing = billingData.billing;
          const agentCounts = billing.current_month.agent_counts;
          
          setDashboardData({
            totalAgents: agentCounts.total,
            activeAgents: agentCounts.total,
            essentialAgents: agentCounts.essential,
            professionalAgents: agentCounts.professional,
            premiumAgents: agentCounts.premium,
            monthlyRevenue: billing.current_month.total_amount,
            annualRevenue: billing.current_month.total_amount * 12,
            upgradeRate: agentCounts.total > 0 ? ((agentCounts.professional + agentCounts.premium) / agentCounts.total * 100) : 0,
            growthRate: 15.8, // Calculate from billing history in future
            planUtilization: billing.max_agents > 0 ? (agentCounts.total / billing.max_agents * 100) : 0,
            recentActivity: []
          });
        } else {
          setError('Failed to load dashboard data');
        }
        */
        
        // Fetch real agent data
        if (brokerageId) {
          console.log('📊 [DASHBOARD] Fetching agent data for brokerage:', brokerageId);
          console.log('📊 [DASHBOARD] Auth token exists:', !!localStorage.getItem('auth_token'));
          
          try {
            const agentList = await BrokerApiService.getAgents(brokerageId);
            
            if (agentList && Array.isArray(agentList)) {
              setAgents(agentList);
              console.log('📊 [DASHBOARD] Received agents:', agentList.length);
              
              // Calculate agent counts by plan
              const planCounts = agentList.reduce((acc: any, agent: any) => {
                const plan = agent.individual_plan || 'essential';
                acc[plan] = (acc[plan] || 0) + 1;
                return acc;
              }, {});
              
              // Calculate active agents
              const activeAgentCount = agentList.filter((agent: any) => agent.is_active).length;

              // Calculate tier-based monthly revenue
              const tierPricing = calculateMonthlyTotal(agentList.length, false);
              const currentTier = getCurrentTier(agentList.length);
              const monthlyRev = tierPricing.total;

              console.log('📊 [DASHBOARD] Calculated costs (tier-based):', {
                tier: currentTier,
                tierName: tierPricing.tierName,
                baseFee: tierPricing.tierPrice,
                agentFees: tierPricing.agentFees,
                perAgentFee: tierPricing.perAgentFee,
                total: monthlyRev
              });
              
              // Calculate upgrade rate (agents on professional or higher plans)
              const upgradedAgents = agentList.filter((agent: any) => 
                ['professional', 'deal_ai', 'agent_kairos', 'premium'].includes(agent.individual_plan)
              ).length;
              
              const upgradePercentage = agentList.length > 0 
                ? (upgradedAgents / agentList.length * 100) 
                : 0;
              
              setDashboardData({
                totalAgents: agentList.length,
                activeAgents: activeAgentCount,
                essentialAgents: planCounts.essential || 0,
                professionalAgents: planCounts.professional || 0,
                dealAIAgents: planCounts.deal_ai || 0,
                agentKairosAgents: planCounts.agent_kairos || 0,
                premiumAgents: planCounts.premium || 0, // Legacy support
                monthlyRevenue: monthlyRev,
                annualRevenue: monthlyRev * 12,
                upgradeRate: upgradePercentage,
                growthRate: 15.8, // TODO: Calculate from historical data
                planUtilization: 50, // TODO: Get max agents from brokerage plan
                recentActivity: [], // TODO: Track agent activity
                baseFee: tierPricing.tierPrice,
                agentFees: tierPricing.agentFees,
                perAgentFee: tierPricing.perAgentFee,
                tierName: tierPricing.tierName
              });
              
              console.log('📊 [DASHBOARD] Processed agent data (tier-based):', {
                total: agentList.length,
                active: activeAgentCount,
                planCounts,
                tier: tierPricing.tierName,
                monthlyRevenue: monthlyRev,
                calculation: `Tier: ${tierPricing.tierName} ($${tierPricing.tierPrice}) + (${agentList.length} agents × $${tierPricing.perAgentFee}) = $${monthlyRev}`
              });
            } else {
              console.log('📊 [DASHBOARD] No agents found or invalid response');
              // Set minimum values with base fee (small tier, 0 agents)
              const minTierPricing = calculateMonthlyTotal(0, false);
              setDashboardData(prev => ({
                ...prev,
                monthlyRevenue: minTierPricing.tierPrice, // Small tier base fee
                annualRevenue: minTierPricing.tierPrice * 12
              }));
            }
          } catch (agentError: any) {
            console.error('📊 [DASHBOARD] Error fetching agents:', agentError);
            // Don't set error for agent fetching, just use defaults
          }
        }
        
      } catch (error: any) {
        console.error('Error fetching dashboard data:', error);
        
        if (error.message.includes('404') || error.message.includes('Brokerage not found')) {
          setError('No brokerage account found. Please complete your brokerage setup.');
        } else if (error.message.includes('timeout')) {
          setError('Server is temporarily unavailable. Please try again later.');
        } else {
          setError('Unable to connect to server - please check your connection');
        }
      }
    };

    if (!loading) {
      fetchDashboardData();
    }
  }, [brokerageId, loading]);

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: any;
    change?: number;
    changeLabel?: string;
    color?: 'primary' | 'success' | 'warning' | 'info';
    subValue?: string;
  }> = ({ title, value, icon, change, changeLabel, color = 'primary', subValue }) => {
    const colorMap = {
      primary: { bg: 'rgba(0, 212, 255, 0.1)', border: 'rgba(0, 212, 255, 0.3)', text: '#00d4ff' },
      success: { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', text: '#10b981' },
      warning: { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', text: '#f59e0b' },
      info: { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)', text: '#3b82f6' }
    };

    const colors = colorMap[color];

    return (
      <Card 
        className="h-100 border-0 ai-hover-lift ai-transition"
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Card.Body className="p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: '48px',
                height: '48px',
                background: `linear-gradient(135deg, ${colors.text}, ${colors.text}cc)`,
                boxShadow: `0 0 20px ${colors.text}40`
              }}
            >
              <FontAwesomeIcon 
                icon={icon} 
                className="text-white" 
                size="lg"
                style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
              />
            </div>
            {change !== undefined && (
              <Badge 
                bg={change >= 0 ? 'success' : 'danger'}
                className="d-flex align-items-center"
                style={{ fontSize: '11px' }}
              >
                <FontAwesomeIcon 
                  icon={change >= 0 ? faArrowUp : faArrowDown} 
                  className="me-1" 
                  size="xs"
                />
                {Math.abs(change)}%
              </Badge>
            )}
          </div>
          
          <div>
            <h3 
              className="fw-bold mb-1"
              style={{ 
                color: colors.text,
                fontSize: '2.2rem',
                textShadow: `0 0 10px ${colors.text}30`
              }}
            >
              {value}
            </h3>
            <p className="text-muted mb-0" style={{ fontSize: '14px', fontWeight: 500 }}>
              {title}
            </p>
            {subValue && (
              <small className="text-muted" style={{ fontSize: '12px' }}>
                {subValue}
              </small>
            )}
            {changeLabel && (
              <small className="text-muted d-block mt-1" style={{ fontSize: '12px' }}>
                {changeLabel}
              </small>
            )}
          </div>
        </Card.Body>
      </Card>
    );
  };

  const AgentDistributionCard: React.FC = () => (
    <Card 
      className="h-100 border-0 ai-enhanced-card"
      style={{ minHeight: '300px' }}
    >
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 16px 0 0'
        }}
      >
        <div className="d-flex align-items-center">
          <FontAwesomeIcon 
            icon={faUsers} 
            className="me-2"
            style={{ color: '#00d4ff', fontSize: '20px' }}
          />
          <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
            Agent Plan Distribution
          </h5>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          {[
            { plan: 'Essential', count: dashboardData.essentialAgents, color: '#6b7280', icon: faBuilding },
            { plan: 'Professional', count: dashboardData.professionalAgents, color: '#00d4ff', icon: faGraduationCap },
            { plan: 'Deal AI', count: dashboardData.dealAIAgents, color: '#3b82f6', icon: faHandHoldingDollar },
            { plan: 'Agent Kairos', count: dashboardData.agentKairosAgents, color: '#10b981', icon: faCrown }
          ].map((item, index) => (
            <div key={index} className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{
                    width: '32px',
                    height: '32px',
                    background: `${item.color}20`,
                    border: `2px solid ${item.color}40`
                  }}
                >
                  <FontAwesomeIcon 
                    icon={item.icon} 
                    style={{ color: item.color, fontSize: '14px' }}
                  />
                </div>
                <div>
                  <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                    {item.plan}
                  </p>
                  <small className="text-muted">
                    {dashboardData.totalAgents > 0 ? `${((item.count / dashboardData.totalAgents) * 100).toFixed(1)}% of agents` : '0% of agents'}
                  </small>
                </div>
              </div>
              <div className="text-end">
                <div className="fw-bold" style={{ color: item.color, fontSize: '18px' }}>
                  {item.count}
                </div>
                <small className="text-muted">agents</small>
              </div>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );

  const RevenueProjectionCard: React.FC = () => (
    <Card 
      className="h-100 border-0 ai-enhanced-card"
      style={{ minHeight: '300px' }}
    >
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 16px 0 0'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faHandHoldingDollar} 
              className="me-2"
              style={{ color: '#10b981', fontSize: '20px' }}
            />
            <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
              Monthly Broker Costs
            </h5>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-end mb-2">
            <span className="text-muted" style={{ fontSize: '14px' }}>Total Monthly Cost</span>
            <span className="fw-bold text-success" style={{ fontSize: '24px' }}>
              ${dashboardData.monthlyRevenue.toLocaleString()}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-end">
            <span className="text-muted" style={{ fontSize: '14px' }}>Annual Cost Projection</span>
            <span className="fw-bold text-success" style={{ fontSize: '18px' }}>
              ${dashboardData.annualRevenue.toLocaleString()}
            </span>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Platform Access (Base)</small>
              <small className="fw-semibold">${dashboardData.baseFee.toLocaleString()}</small>
            </div>
            <ProgressBar
              now={dashboardData.monthlyRevenue > 0 ? (dashboardData.baseFee / dashboardData.monthlyRevenue * 100) : 0} 
              style={{ height: '6px' }}
              className="mb-2"
            />
          </div>
          
          <div>
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Agent Fees ({dashboardData.totalAgents} × ${dashboardData.perAgentFee})</small>
              <small className="fw-semibold">${dashboardData.agentFees.toLocaleString()}</small>
            </div>
            <ProgressBar
              now={dashboardData.monthlyRevenue > 0 ? (dashboardData.agentFees / dashboardData.monthlyRevenue * 100) : 0} 
              variant="info"
              style={{ height: '6px' }}
              className="mb-2"
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const RecentActivityCard: React.FC = () => (
    <Card 
      className="h-100 border-0 ai-enhanced-card"
      style={{ minHeight: '300px' }}
    >
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 16px 0 0'
        }}
      >
        <div className="d-flex align-items-center">
          <FontAwesomeIcon 
            icon={faCalendarAlt} 
            className="me-2"
            style={{ color: '#3b82f6', fontSize: '20px' }}
          />
          <h5 className="mb-0 fw-semibold" style={{ color: '#1f2937' }}>
            Recent Activity
          </h5>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-3">
          {dashboardData.recentActivity.map((activity, index) => (
            <div key={index} className="d-flex align-items-center p-3 rounded-3" 
                 style={{ background: 'rgba(0, 212, 255, 0.05)' }}>
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{
                  width: '36px',
                  height: '36px',
                  background: activity.type === 'upgrade' ? '#10b981' : '#00d4ff',
                  color: 'white'
                }}
              >
                <FontAwesomeIcon 
                  icon={activity.type === 'upgrade' ? faArrowTrendUp : faUsers} 
                  size="sm"
                />
              </div>
              <div className="flex-grow-1">
                <p className="mb-1 fw-semibold" style={{ fontSize: '14px' }}>
                  {activity.type === 'upgrade' ? 'Plan Upgrade' : 'New Agent'}
                </p>
                <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                  <strong>{activity.agent}</strong> {activity.type === 'upgrade' ? 'upgraded to' : 'joined with'} {activity.plan}
                </p>
              </div>
              <small className="text-muted">{activity.date}</small>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <div className="ai-loading-indicator">
        <div className="ai-loading-spinner me-3"></div>
        <span className="text-muted">Loading dashboard data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-error-indicator">
        <FontAwesomeIcon icon={faBolt} className="text-danger me-2" />
        <span className="text-danger">{error}</span>
      </div>
    );
  }

  // Show message if no brokerage ID
  if (!brokerageId) {
    return (
      <div className="text-center p-5">
        <FontAwesomeIcon icon={faBuilding} className="text-muted mb-3" size="3x" />
        <h5 className="text-muted">No Brokerage Selected</h5>
        <p className="text-muted">Please ensure you are logged in with a brokerage account.</p>
      </div>
    );
  }

  return (
    <div className="broker-dashboard">
      {/* Metrics Cards Row */}
      <Row className="g-4 mb-4">
        <Col lg={3} md={6}>
          <MetricCard
            title="Total Agents"
            value={dashboardData.totalAgents}
            icon={faUsers}
            change={15.8}
            changeLabel="vs last month"
            color="primary"
            subValue={`${dashboardData.activeAgents} active`}
          />
        </Col>
        <Col lg={3} md={6}>
          <MetricCard
            title="Monthly Broker Costs"
            value={`$${dashboardData.monthlyRevenue.toLocaleString()}`}
            icon={faDollarSign}
            change={22.5}
            changeLabel="vs last month"
            color="success"
            subValue={`Base: $${dashboardData.baseFee.toLocaleString()} + Agents: $${dashboardData.agentFees.toLocaleString()}`}
          />
        </Col>
        <Col lg={3} md={6}>
          <MetricCard
            title="Upgrade Rate"
            value={`${dashboardData.upgradeRate.toFixed(1)}%`}
            icon={faRocket}
            change={8.3}
            changeLabel="vs last month"
            color="warning"
            subValue={`${dashboardData.dealAIAgents + dashboardData.professionalAgents + dashboardData.agentKairosAgents} agents with AI features`}
          />
        </Col>
        <Col lg={3} md={6}>
          <MetricCard
            title="Active Agents"
            value={dashboardData.activeAgents}
            icon={faChartLine}
            change={0}
            changeLabel=""
            color="info"
            subValue={`${dashboardData.totalAgents} total agents registered`}
          />
        </Col>
      </Row>

      {/* Charts and Analytics Row */}
      <Row className="g-4 mb-4">
        <Col lg={4}>
          <AgentDistributionCard />
        </Col>
        <Col lg={4}>
          <RevenueProjectionCard />
        </Col>
        <Col lg={4}>
          <RecentActivityCard />
        </Col>
      </Row>

      {/* Transactions Row */}
      <Row className="g-4">
        <Col lg={8}>
          <ActiveTransactionsWidget brokerageId={brokerageId || ''} />
        </Col>
        <Col lg={4}>
          <Card className="h-100 ai-enhanced-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faFileContract} className="me-2 text-primary" />
                Quick Actions
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  onClick={() => window.location.href = '/apps/broker-portal'}
                  className="ai-hover-lift"
                >
                  <FontAwesomeIcon icon={faFileContract} className="me-2" />
                  View All Transactions
                </Button>
                <Button 
                  variant="outline-primary" 
                  onClick={() => window.location.href = '/broker-intelligence/documents'}
                  className="ai-hover-lift"
                >
                  <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
                  Document Templates
                </Button>
                <Button 
                  variant="outline-primary" 
                  onClick={() => window.location.href = '/broker-intelligence/ai-review'}
                  className="ai-hover-lift"
                >
                  <FontAwesomeIcon icon={faBolt} className="me-2" />
                  AI Document Review
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default BrokerDashboard;